import { HIERARCHY_SHIPPING_BULK_ORDER_SORTS } from '../../../../constants/hierarchy-shipping-settings';

const getTransactionItemImageUrl = transactionItem =>
  (transactionItem?.print_product_configurations?.records ?? [])
    .map(
      config =>
        config?.front_print_file_url ??
        config?.account_card?.front_preview_file_url,
    )
    .find(Boolean) ?? null;

const getTransactionItemLines = (transactionItem, print) => {
  const printItems = transactionItem?.print_items?.records ?? [];
  if (printItems.length) {
    return printItems.map((printItem, index) => ({
      key: `${transactionItem.id}-item-${index}`,
      quantity: printItem?.quantity ?? 1,
      name: printItem?.print_package_item_type_name ?? 'Print Products',
    }));
  }

  const configs = transactionItem?.print_product_configurations?.records ?? [];
  if (configs.length) {
    return configs.map((config, index) => ({
      key: `${transactionItem.id}-config-${index}`,
      quantity: config?.quantity ?? 1,
      name: config?.print_product_configuration?.name ?? 'Print Products',
    }));
  }

  return [
    {
      key: transactionItem?.id ?? print.id,
      quantity: print?.quantity ?? 1,
      name:
        transactionItem?.custom_print_name ||
        transactionItem?.photo_print_size_name ||
        transactionItem?.photo_pricing_name ||
        print?.name ||
        print?.custom_print?.name ||
        'Print Products',
    },
  ];
};

const getTransactionItemGroup = (transactionItem, print) => {
  const lines = getTransactionItemLines(transactionItem, print);
  const packageName =
    transactionItem?.print_package_name ||
    transactionItem?.photo_pricing_name ||
    null;

  return {
    key: transactionItem?.id ?? print.id,
    isPackage: !!transactionItem?.print_package_name || lines.length > 1,
    packageName,
    lines,
  };
};

/*
 * Mirrors hive-next `getNameForBulkShippingOrder`: the identity attached to
 * any of the transaction's items (print package, custom print or photo
 * pricing) wins when it has both names, otherwise the account name.
 */
export const getOrderName = transaction => {
  const items = transaction?.transaction_items?.records ?? [];

  const identity = items
    .map(item => item?.identity ?? item?.purchased_photo_pricing?.identity)
    .find(candidate => candidate?.name && candidate?.last_name);

  if (identity) {
    return `${identity.name} ${identity.last_name}`;
  }

  return transaction?.account?.name || '';
};

/*
 * Mirrors hive-next `getNameParts`: the last token is the last name, the
 * rest is the first name, and a first name of "Me" is dropped.
 */
export const getNameParts = name => {
  const nameParts = (name || '').trim().replace(/\s\s+/g, ' ').split(' ');

  const firstName =
    nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0];
  const lastName =
    nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined;

  return {
    firstName: firstName?.toLowerCase() !== 'me' ? firstName : undefined,
    lastName,
  };
};

/*
 * Mirrors hive-next `compareNames`: empty names sort last, otherwise a
 * case-insensitive comparison.
 */
export const compareNames = (a, b) => {
  if (!a || !b) {
    return (a ? 0 : 1) - (b ? 0 : 1);
  }

  return a.localeCompare(b, 'en', { sensitivity: 'base' });
};

/*
 * Mirrors hive-next `sortOrders`. `orders` must already be in created_at
 * ascending order, which doubles as the tiebreaker for the name sorts.
 */
export const sortOrders = (orders, bulkOrderSort) => {
  switch (bulkOrderSort) {
    case HIERARCHY_SHIPPING_BULK_ORDER_SORTS.CREATED_AT_DESC:
      return [...orders].reverse();
    case HIERARCHY_SHIPPING_BULK_ORDER_SORTS.FIRST_NAME_ASC:
      return [...orders].sort(
        (a, b) =>
          compareNames(a.firstName, b.firstName) ||
          compareNames(a.lastName, b.lastName),
      );
    case HIERARCHY_SHIPPING_BULK_ORDER_SORTS.LAST_NAME_ASC:
      return [...orders].sort(
        (a, b) =>
          compareNames(a.lastName, b.lastName) ||
          compareNames(a.firstName, b.firstName),
      );
    default:
      return orders;
  }
};

const getCreatedAtTime = record =>
  record?.created_at ? new Date(record.created_at).getTime() : 0;

export const getBulkShippingPrints = (transaction, bulkShippingId) =>
  (transaction?.purchased_custom_prints?.records ?? []).filter(
    print => print?.album_folder_bulk_shipping?.id === bulkShippingId,
  );

const buildRow = (transaction, prints) => {
  const customerName = getOrderName(transaction);
  const { firstName, lastName } = getNameParts(customerName);
  const albumNames = [
    ...new Set(prints.map(print => print?.album?.name).filter(Boolean)),
  ];
  const seenTransactionItems = new Set();
  const itemGroups = [];
  let imageUrl = null;

  prints.forEach(print => {
    if (!imageUrl) {
      imageUrl = getTransactionItemImageUrl(print?.transaction_item);
    }

    const transactionItemId = print?.transaction_item?.id;
    if (transactionItemId) {
      if (seenTransactionItems.has(transactionItemId)) {
        return;
      }
      seenTransactionItems.add(transactionItemId);
    }

    itemGroups.push(getTransactionItemGroup(print?.transaction_item, print));
  });

  return {
    key: transaction.id,
    createdAt: transaction.created_at,
    group: albumNames.length ? albumNames.join(', ') : '—',
    customerName: customerName || '—',
    firstName,
    lastName,
    customerEmail:
      transaction?.account?.email_address ?? transaction?.email_address ?? '—',
    customerPhone: transaction?.account?.phone_number ?? '—',
    imageUrl,
    itemGroups,
  };
};

/*
 * One row per order (transaction), limited to the prints that belong to the
 * given bulk shipping, in the same order the lab receives them.
 */
export const buildRows = (transactions, bulkShippingId, bulkOrderSort) => {
  const rows = [...(transactions ?? [])]
    .sort((a, b) => getCreatedAtTime(a) - getCreatedAtTime(b))
    .map(transaction => ({
      transaction,
      prints: getBulkShippingPrints(transaction, bulkShippingId),
    }))
    .filter(({ prints }) => prints.length > 0)
    .map(({ transaction, prints }) => buildRow(transaction, prints));

  return sortOrders(rows, bulkOrderSort);
};
